/**
 * Identity section — shared by every user type. Editable: full name, gender,
 * date of birth. Read-only: email and mobile (identity anchors that require a
 * separate re-verification flow, so they're shown but not editable here).
 *
 * Saving goes through AuthContext.updateProfile → PUT /auth/profile, which the
 * controller allow-list now accepts.
 */

import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { EditableFieldsCard } from './ProfilePrimitives';
import { Select, inputSmClass } from '../common/FormControls';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function dobToText(dob) {
  if (!dob || !dob.year) return null;
  const m = Number(dob.month);
  const monthLabel = m >= 1 && m <= 12 ? MONTHS[m - 1] : dob.month;
  return [dob.day, monthLabel, dob.year].filter(Boolean).join(' ');
}

export default function IdentitySection({ user }) {
  const { updateProfile } = useAuth();
  const { showToast } = useUI();

  const fields = [
    { name: 'fullName', label: 'Full Name', placeholder: 'e.g. Ananya Sharma' },
    {
      name: 'email',
      label: 'Email',
      editable: false,
      readValue: (v) => v?.email,
    },
    {
      name: 'mobile',
      label: 'Mobile',
      editable: false,
      readValue: (v) => (v?.mobile ? `+91 ${v.mobile}` : null),
    },
    {
      name: 'gender',
      label: 'Gender',
      type: 'select',
      options: [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' },
      ],
      readValue: (v) => (v?.gender ? v.gender.charAt(0).toUpperCase() + v.gender.slice(1) : null),
    },
    {
      name: 'dateOfBirth',
      label: 'Date of Birth',
      type: 'custom',
      readValue: (v) => dobToText(v?.dateOfBirth),
      renderEdit: (value, onChange) => {
        const dob = value || {};
        const set = (key, val) => onChange({ ...dob, [key]: val });
        return (
          <div className="flex items-center gap-2">
            <input
              type="text" inputMode="numeric" maxLength={2} placeholder="DD"
              value={dob.day ?? ''} onChange={(e) => set('day', e.target.value)}
              className={`${inputSmClass} w-[58px]`}
            />
            <div className="flex-1">
              {/* Month values stay STRINGS on both sides — a stored numeric
                  month would otherwise never match its option and the picker
                  would open blank on an already-saved date of birth. */}
              <Select
                size="sm"
                value={dob.month == null || dob.month === '' ? '' : String(dob.month)}
                onChange={(v) => set('month', v)}
                placeholder="Month"
                options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))}
              />
            </div>
            <input
              type="text" inputMode="numeric" maxLength={4} placeholder="YYYY"
              value={dob.year ?? ''} onChange={(e) => set('year', e.target.value)}
              className={`${inputSmClass} w-[74px]`}
            />
          </div>
        );
      },
    },
  ];

  const handleSave = async (draft) => {
    const payload = { ...draft };
    // An empty gender selection means "leave unchanged" — never send '' to an
    // enum field (Mongoose would reject it).
    if (!payload.gender) delete payload.gender;
    // Drop an untouched / blank date of birth.
    const dob = payload.dateOfBirth;
    if (!dob || typeof dob !== 'object' || (!dob.day && !dob.month && !dob.year)) {
      delete payload.dateOfBirth;
    }

    const res = await updateProfile(payload);
    if (res?.success) {
      showToast('Profile updated', 'success');
    } else {
      throw new Error(res?.message || 'Update failed');
    }
  };

  return (
    <EditableFieldsCard
      title="Account Identity"
      subtitle="Your basic identity details. Email and mobile changes require re-verification."
      icon="🪪"
      fields={fields}
      values={user}
      onSave={handleSave}
    />
  );
}
